import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getCourse } from '../../utils/studyMaterialsApi';

function flattenLessons(tree) {
  const flat = [];
  (tree || []).forEach((cat) => {
    (cat.mainTitles || []).forEach((mt) => {
      (mt.subTitles || []).forEach((st) => {
        flat.push({ cat, mt, st });
      });
    });
  });
  return flat;
}

// Converts lesson HTML into plain lowercase text once per lesson, so the
// search box can match against the actual written content, not just titles.
function stripHtml(html) {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || div.innerText || '').toLowerCase();
}

function buildSearchIndex(flatLessons) {
  const index = new Map();
  flatLessons.forEach(({ st }) => {
    const text = [st.title, stripHtml(st.content?.en), stripHtml(st.content?.ta)]
      .join(' ')
      .toLowerCase();
    index.set(st.id, text);
  });
  return index;
}

function filterTree(tree, query, searchIndex) {
  const q = query.trim().toLowerCase();
  if (!q) return tree || [];
  return (tree || [])
    .map((cat) => {
      const catMatches = cat.name.toLowerCase().includes(q);
      const mainTitles = (cat.mainTitles || [])
        .map((mt) => {
          const mtMatches = mt.title.toLowerCase().includes(q);
          const subTitles = (mt.subTitles || []).filter((st) => {
            if (catMatches || mtMatches) return true;
            const indexed = searchIndex?.get(st.id);
            return indexed ? indexed.includes(q) : st.title.toLowerCase().includes(q);
          });
          return subTitles.length > 0 ? { ...mt, subTitles } : null;
        })
        .filter(Boolean);
      return mainTitles.length > 0 ? { ...cat, mainTitles } : null;
    })
    .filter(Boolean);
}

const STATUS_BADGE = {
  draft: 'bg-amber-100 text-amber-700',
  published: 'bg-emerald-100 text-emerald-700',
};

const READ_MODE_KEY = 'sm_admin_blog_read_mode';

// Rewrites raw lesson HTML into a string that reads smoothly with a
// screen-reader / text-to-speech tool: code blocks and lists become plain
// flowing sentences, short consecutive paragraphs are merged, decorative
// <hr>s are dropped, and ellipses/emoji (which TTS engines stumble over) are
// stripped. Works on a detached, parsed copy of the HTML (not the live,
// on-screen DOM) so switching read mode on/off is just swapping which HTML
// string gets rendered -- no imperative apply/revert step that could be
// skipped or left half-done depending on effect timing.
function readModeHtml(rawHtml) {
  if (!rawHtml) return rawHtml;
  const doc = new DOMParser().parseFromString(rawHtml, 'text/html');
  const container = doc.body;

  container.querySelectorAll('hr').forEach((hr) => hr.remove());

  container.querySelectorAll('pre').forEach((pre) => {
    const codeEl = pre.querySelector('code');
    const text = (codeEl ? codeEl.textContent : pre.textContent || '').trim();
    const div = doc.createElement('div');
    div.className = 'sfy-pre';
    div.textContent = text;
    pre.replaceWith(div);
  });

  container.querySelectorAll('ul, ol').forEach((list) => {
    const items = [...list.querySelectorAll('li')].map((li) => (li.textContent || '').trim()).filter(Boolean);
    const p = doc.createElement('p');
    p.textContent = `${items.join(', ')}.`;
    list.replaceWith(p);
  });

  // Merge consecutive top-level paragraphs into one, in place of the first.
  let group = [];
  const flushPGroup = () => {
    if (group.length < 2) { group = []; return; }
    const merged = doc.createElement('p');
    merged.textContent = group.map((p) => (p.textContent || '').trim()).filter(Boolean).join(' ');
    group[0].replaceWith(merged);
    group.slice(1).forEach((p) => p.remove());
    group = [];
  };
  [...container.children].forEach((el) => {
    if (el.tagName === 'P') group.push(el);
    else flushPGroup();
  });
  flushPGroup();

  const walker = doc.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  let node = walker.nextNode();
  while (node) { textNodes.push(node); node = walker.nextNode(); }
  textNodes.forEach((n) => {
    n.textContent = n.textContent.replace(/\.{2,}|…/g, '.').replace(/\p{Extended_Pictographic}/gu, '');
  });

  return container.innerHTML;
}

// Wraps every <pre> in the freshly-rendered lesson content with a small
// "Copy" button. Returns a cleanup function that removes the listeners
// (the wrapper elements themselves are discarded automatically the next
// time React replaces the container's innerHTML for a new lesson/language).
function addCopyButtons(container) {
  if (!container) return () => {};
  const teardown = [];

  container.querySelectorAll('pre').forEach((pre) => {
    if (pre.dataset.copyBtnAdded) return;
    pre.dataset.copyBtnAdded = '1';

    const wrapper = document.createElement('div');
    wrapper.className = 'pre-wrap';
    pre.parentNode.insertBefore(wrapper, pre);
    wrapper.appendChild(pre);

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'copy-btn';
    btn.textContent = 'Copy';

    let resetTimer = null;
    const handleClick = () => {
      const codeEl = pre.querySelector('code');
      const text = (codeEl ? codeEl.innerText : pre.innerText).trim();
      navigator.clipboard.writeText(text).then(() => {
        btn.textContent = 'Copied!';
        btn.classList.add('copied');
      }).catch(() => {
        btn.textContent = 'Failed';
      });
      clearTimeout(resetTimer);
      resetTimer = setTimeout(() => {
        btn.textContent = 'Copy';
        btn.classList.remove('copied');
      }, 1500);
    };
    btn.addEventListener('click', handleClick);
    wrapper.appendChild(btn);

    teardown.push(() => {
      clearTimeout(resetTimer);
      btn.removeEventListener('click', handleClick);
    });
  });

  return () => teardown.forEach((fn) => fn());
}

// Extends the current text selection's start point through to the very end
// of `container`'s content — used by the Ctrl+Down "select to end" shortcut.
// Falls back to selecting from the very start if nothing is currently
// selected inside the container.
function selectFromCursorToEnd(container) {
  if (!container) return;
  const selection = window.getSelection();
  if (!selection) return;

  let anchorNode = selection.anchorNode;
  let anchorOffset = selection.anchorOffset;
  if (!anchorNode || !container.contains(anchorNode)) {
    anchorNode = container;
    anchorOffset = 0;
  }

  const range = document.createRange();
  try {
    range.setStart(anchorNode, anchorOffset);
  } catch {
    range.setStart(container, 0);
  }

  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
  let lastTextNode = null;
  while (walker.nextNode()) lastTextNode = walker.currentNode;
  if (lastTextNode) {
    range.setEnd(lastTextNode, lastTextNode.textContent.length);
  } else {
    range.setEnd(container, container.childNodes.length);
  }

  selection.removeAllRanges();
  selection.addRange(range);
}

function LoadingSpinner() {
  return (
    <div className="flex h-screen flex-col items-center justify-center text-slate-400">
      <svg className="h-8 w-8 animate-spin text-violet-500" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v8H4z" />
      </svg>
      <p className="mt-3 text-sm">Loading course…</p>
    </div>
  );
}

export default function StudyMaterialBlogView() {
  const { id: courseId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [activeLessonId, setActiveLessonId] = useState(null);
  const [expandedCatId, setExpandedCatId] = useState(null);
  const [search, setSearch] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [lang, setLang] = useState('en');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [readMode, setReadMode] = useState(false);
  const articleRef = useRef(null);
  const contentRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const data = await getCourse(courseId);
        if (cancelled) return;
        if (!data) { setError('Course not found.'); return; }
        setCourse(data);
        const flat = flattenLessons(data.tree);
        if (flat.length > 0) {
          const lessonParam = searchParams.get('lesson');
          const match = lessonParam ? flat.find((l) => l.st.id === lessonParam) : null;
          const initial = match || flat[0];
          setActiveLessonId(initial.st.id);
          setExpandedCatId(initial.cat.id);
        }
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.message || 'Failed to load this course.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // Only re-run when the course itself changes — searchParams is read once
    // here for the initial deep link, not tracked afterward.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const flatLessons = useMemo(() => flattenLessons(course?.tree), [course]);
  const activeIndex = useMemo(
    () => flatLessons.findIndex((l) => l.st.id === activeLessonId),
    [flatLessons, activeLessonId]
  );
  const active = activeIndex >= 0 ? flatLessons[activeIndex] : null;
  const prevLesson = activeIndex > 0 ? flatLessons[activeIndex - 1] : null;
  const nextLesson = activeIndex >= 0 && activeIndex < flatLessons.length - 1 ? flatLessons[activeIndex + 1] : null;

  const searchIndex = useMemo(() => buildSearchIndex(flatLessons), [flatLessons]);
  const visibleTree = useMemo(() => filterTree(course?.tree, search, searchIndex), [course, search, searchIndex]);

  const hasEnglish = Boolean(active?.st.content?.en?.trim());
  const hasTamil = Boolean(active?.st.content?.ta?.trim());
  const effectiveLang = lang === 'ta' && hasTamil ? 'ta' : hasEnglish ? 'en' : (hasTamil ? 'ta' : 'en');
  const rawContent = active?.st.content?.[effectiveLang] || '';
  const displayHtml = useMemo(
    () => (readMode ? readModeHtml(rawContent) : rawContent),
    [readMode, rawContent]
  );

  const selectLesson = (catId, lessonId) => {
    setActiveLessonId(lessonId);
    setExpandedCatId(catId);
    setSidebarOpen(false);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('lesson', lessonId);
      return next;
    }, { replace: true });
    articleRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleArticleScroll = (e) => setShowScrollTop(e.target.scrollTop > 400);
  const scrollToTop = () => articleRef.current?.scrollTo({ top: 0, behavior: 'smooth' });

  useEffect(() => {
    if (localStorage.getItem(READ_MODE_KEY) === '1') setReadMode(true);
  }, []);

  useEffect(() => {
    localStorage.setItem(READ_MODE_KEY, readMode ? '1' : '0');
  }, [readMode]);

  // Adds "Copy" buttons to every code block in the freshly-rendered lesson.
  // Re-runs on readMode too: read mode's HTML has no <pre> tags at all (see
  // readModeHtml below), so this naturally finds nothing to wrap there, and
  // re-attaches buttons when switching back since the DOM was just replaced.
  useEffect(() => {
    return addCopyButtons(contentRef.current);
  }, [active?.st.id, effectiveLang, readMode]);

  // Ctrl+Down selects from wherever the current text selection starts
  // through to the end of the lesson content, mirroring the "select to end"
  // shortcut some readers/editors offer.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!e.ctrlKey || e.key !== 'ArrowDown') return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (!contentRef.current) return;
      e.preventDefault();
      selectFromCursorToEnd(contentRef.current);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // "/" focuses the course search box from anywhere on the page (unless
  // already typing in another field), matching the shortcut hint shown in
  // its placeholder.
  useEffect(() => {
    const handleSlash = (e) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      e.preventDefault();
      searchInputRef.current?.focus();
    };
    document.addEventListener('keydown', handleSlash);
    return () => document.removeEventListener('keydown', handleSlash);
  }, []);

  // Dragging a text selection near the top/bottom edge of the article pane
  // auto-scrolls it, instead of requiring the mouse wheel while the
  // selection drag is still in progress — mixing wheel input with an active
  // drag-select is what was collapsing the selection, since the browser's
  // selection-extension logic tracks mousemove, not scroll events.
  useEffect(() => {
    const container = articleRef.current;
    if (!container) return undefined;

    const EDGE = 60;
    const MAX_SPEED = 18;
    let pointerY = null;
    let frame = null;

    const tick = () => {
      if (pointerY == null) { frame = null; return; }
      const rect = container.getBoundingClientRect();
      let delta = 0;
      if (pointerY < rect.top + EDGE) {
        delta = -Math.ceil(((rect.top + EDGE - pointerY) / EDGE) * MAX_SPEED);
      } else if (pointerY > rect.bottom - EDGE) {
        delta = Math.ceil(((pointerY - (rect.bottom - EDGE)) / EDGE) * MAX_SPEED);
      }
      if (delta !== 0) {
        container.scrollTop += delta;
        frame = requestAnimationFrame(tick);
      } else {
        frame = null;
      }
    };

    const handleMouseMove = (e) => {
      if (e.buttons !== 1) { pointerY = null; return; }
      pointerY = e.clientY;
      if (!frame) frame = requestAnimationFrame(tick);
    };

    const stop = () => {
      pointerY = null;
      if (frame) cancelAnimationFrame(frame);
      frame = null;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', stop);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', stop);
      stop();
    };
  }, []);

  if (loading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3 px-6 text-center text-sm text-slate-500">
        <p>{error}</p>
        <button onClick={() => navigate('/admin/study-materials')} className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white">
          Back to Study Materials
        </button>
      </div>
    );
  }

  if (!course || flatLessons.length === 0) {
    return (
      <div className="flex h-screen items-center justify-center px-6 text-center text-sm text-slate-500">
        This course doesn't have any lessons yet.
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex h-screen w-80 shrink-0 flex-col border-r border-slate-200 bg-white transition-transform lg:static lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="border-b border-slate-100 px-4 py-4">
          <div className="line-clamp-2 text-sm font-bold leading-snug text-slate-800">{course.title}</div>
          <div className="mt-1.5 flex items-center gap-2 text-xs text-slate-500">
            <span>{flatLessons.length} {flatLessons.length === 1 ? 'lesson' : 'lessons'}</span>
            <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase ${STATUS_BADGE[course.status] || STATUS_BADGE.draft}`}>
              {course.status}
            </span>
          </div>
        </div>

        <div className="relative px-4 pb-1 pt-3">
          <svg className="pointer-events-none absolute left-7 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
          </svg>
          <input
            ref={searchInputRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search entire course… ( / )"
            className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-8 text-sm outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
          />
          {!search && (
            <kbd className="pointer-events-none absolute right-6 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded border border-slate-200 bg-slate-50 text-[10px] font-semibold text-slate-400">
              /
            </kbd>
          )}
        </div>

        <div className="flex-1 overflow-y-auto overflow-x-hidden px-2 pb-6 pt-2">
          {visibleTree.length === 0 ? (
            <p className="px-3 py-6 text-center text-xs text-slate-400">No lessons match your search.</p>
          ) : (
            visibleTree.map((cat) => {
              const expanded = search.trim() ? true : expandedCatId === cat.id;
              return (
                <div key={cat.id} className="mb-1">
                  <button
                    onClick={() => setExpandedCatId(expanded ? null : cat.id)}
                    className="flex w-full items-center gap-1.5 rounded-lg px-2 py-2 text-left text-xs font-bold uppercase tracking-wide text-violet-700 hover:bg-violet-50 transition"
                  >
                    <svg className={`h-4 w-4 shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 0 1 .02-1.06L11.168 10 7.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 0 1-1.06-.02z" clipRule="evenodd" />
                    </svg>
                    <span className="min-w-0 flex-1 truncate">{cat.name}</span>
                  </button>

                  {expanded && cat.mainTitles.map((mt) => (
                    <div key={mt.id}>
                      <div className="mb-0.5 ml-7 mt-1.5 truncate pr-2 text-xs font-bold text-slate-600">{mt.title}</div>
                      {mt.subTitles.map((st) => {
                        const isActive = st.id === activeLessonId;
                        return (
                          <button
                            key={st.id}
                            onClick={() => selectLesson(cat.id, st.id)}
                            className={`mb-0.5 flex w-full items-center gap-2 rounded-lg py-2 pl-9 pr-2 text-left text-xs transition ${
                              isActive ? 'bg-violet-50 font-semibold text-violet-800' : 'text-slate-600 hover:bg-slate-100'
                            }`}
                          >
                            <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${isActive ? 'bg-violet-500' : 'bg-slate-300'}`} />
                            <span className="min-w-0 flex-1 truncate">{st.title}</span>
                            <span className={`shrink-0 rounded px-1 py-0.5 text-[8px] font-bold uppercase ${STATUS_BADGE[st.status] || STATUS_BADGE.draft}`}>
                              {st.status}
                            </span>
                            {st.hidden && (
                              <svg className="h-3 w-3 shrink-0 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
                              </svg>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              );
            })
          )}
        </div>
      </aside>

      <main className="flex min-w-0 flex-1 flex-col">
        <div className="sticky top-0 z-20 flex items-center gap-3 border-b border-slate-200 bg-white px-5 py-3">
          <button
            onClick={() => setSidebarOpen(true)}
            title="Table of contents"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 lg:hidden"
          >
            <svg className="h-4.5 w-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>
          </button>

          <div className="min-w-0 flex-1 truncate text-xs text-slate-500">
            {!readMode && (active ? `${active.cat.name} / ${active.mt.title} / ${active.st.title}` : course.title)}
          </div>

          <button
            onClick={() => setReadMode((v) => !v)}
            title="Speechify-friendly reading mode"
            className={`flex shrink-0 items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${
              readMode ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-800'
            }`}
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.536 8.464a5 5 0 0 1 0 7.072M18.364 5.636a9 9 0 0 1 0 12.728M6 18l-3 3V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v9l-3-3z" />
            </svg>
            {readMode ? 'Reading On' : 'Read Mode'}
          </button>

          {(hasEnglish && hasTamil) && (
            <div className="flex shrink-0 gap-0.5 rounded-lg bg-slate-100 p-1">
              <button
                onClick={() => setLang('en')}
                className={`rounded-md px-2.5 py-1 text-xs font-semibold transition ${effectiveLang === 'en' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
              >
                EN
              </button>
              <button
                onClick={() => setLang('ta')}
                className={`rounded-md px-2.5 py-1 text-xs font-semibold transition ${effectiveLang === 'ta' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
              >
                TA
              </button>
            </div>
          )}

          <span className="shrink-0 whitespace-nowrap rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700">
            {activeIndex + 1} / {flatLessons.length}
          </span>

          <button
            onClick={() => window.close()}
            title="Close tab"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div ref={articleRef} onScroll={handleArticleScroll} className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="w-full px-6 py-9 sm:px-10">
            {active && (
              <>
                {rawContent.trim() ? (
                  <div
                    ref={contentRef}
                    className="admin-blog-content text-[15.5px] leading-[1.8] text-slate-800"
                    dangerouslySetInnerHTML={{ __html: displayHtml }}
                  />
                ) : (
                  <p className="text-sm italic text-slate-400">Content for this lesson hasn't been added yet.</p>
                )}

                {!readMode && (
                  <div className="mt-12 flex items-start justify-between gap-3 border-t border-slate-200 pt-5">
                    <button
                      disabled={!prevLesson}
                      onClick={() => prevLesson && selectLesson(prevLesson.cat.id, prevLesson.st.id)}
                      className="flex max-w-[48%] items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-xs font-semibold text-slate-600 hover:border-violet-300 hover:text-violet-700 transition disabled:pointer-events-none disabled:opacity-30"
                    >
                      <svg className="h-3 w-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
                      </svg>
                      <span className="truncate">{prevLesson ? prevLesson.st.title : 'No previous lesson'}</span>
                    </button>
                    <button
                      disabled={!nextLesson}
                      onClick={() => nextLesson && selectLesson(nextLesson.cat.id, nextLesson.st.id)}
                      className="ml-auto flex max-w-[48%] items-center justify-end gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-xs font-semibold text-slate-600 hover:border-violet-300 hover:text-violet-700 transition disabled:pointer-events-none disabled:opacity-30"
                    >
                      <span className="truncate">{nextLesson ? nextLesson.st.title : 'No next lesson'}</span>
                      <svg className="h-3 w-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                      </svg>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <button
          onClick={scrollToTop}
          title="Back to top"
          className={`fixed bottom-6 right-6 z-30 flex h-11 w-11 items-center justify-center rounded-full bg-violet-600 text-white shadow-lg shadow-violet-600/30 transition ${
            showScrollTop && !readMode ? 'opacity-100' : 'pointer-events-none translate-y-3 opacity-0'
          }`}
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" />
          </svg>
        </button>
      </main>

      <style>{`
        .admin-blog-content h1 { font-size: 26px; font-weight: 700; margin: 28px 0 12px; color: #111827; }
        .admin-blog-content h2 { font-size: 22px; font-weight: 700; margin: 24px 0 10px; color: #111827; }
        .admin-blog-content h3 { font-size: 19px; font-weight: 700; margin: 20px 0 8px; color: #111827; }
        .admin-blog-content h4 { font-size: 17px; font-weight: 700; margin: 16px 0 6px; color: #111827; }
        .admin-blog-content p { margin: 0 0 16px; }
        .admin-blog-content ul { list-style: disc; margin: 0 0 16px; padding-left: 24px; }
        .admin-blog-content ol { list-style: decimal; margin: 0 0 16px; padding-left: 24px; }
        .admin-blog-content li { margin-bottom: 6px; }
        .admin-blog-content a { color: #7c3aed; text-decoration: underline; }
        .admin-blog-content blockquote {
          border-left: 4px solid #8b5cf6;
          background: #f5f3ff;
          color: #6d28d9;
          padding: 10px 18px;
          margin: 16px 0;
          border-radius: 0 10px 10px 0;
        }
        .admin-blog-content pre {
          background: #0f172a;
          color: #e2e8f0;
          padding: 16px;
          border-radius: 12px;
          overflow-x: auto;
          font-size: 13.5px;
          margin: 16px 0;
        }
        .admin-blog-content code { font-family: 'SFMono-Regular', Consolas, monospace; }
        .admin-blog-content .pre-wrap { position: relative; }
        .admin-blog-content .copy-btn {
          position: absolute;
          top: 10px;
          right: 10px;
          padding: 4px 10px;
          font-size: 11px;
          font-weight: 600;
          color: #cbd5e1;
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.18);
          border-radius: 6px;
          cursor: pointer;
          transition: background 0.15s, color 0.15s, border-color 0.15s;
        }
        .admin-blog-content .copy-btn:hover { background: rgba(255, 255, 255, 0.16); color: #fff; }
        .admin-blog-content .copy-btn.copied { color: #34d399; border-color: rgba(52, 211, 153, 0.4); }
        .admin-blog-content img { max-width: 100%; border-radius: 12px; margin: 16px 0; }
        .admin-blog-content hr { border: none; border-top: 1px solid #e5e7eb; margin: 28px 0; }
        .admin-blog-content table { width: 100%; border-collapse: collapse; margin: 16px 0; font-size: 14px; }
        .admin-blog-content td, .admin-blog-content th { border: 1px solid #e5e7eb; padding: 8px 10px; }
        .admin-blog-content iframe { max-width: 100%; border-radius: 12px; }

        .admin-blog-content .sfy-pre {
          display: block;
          font-family: inherit;
          font-size: 0.95em;
          line-height: 1.85;
          color: #374151;
          background: #f8fafc;
          border-left: 3px solid #94a3b8;
          border-radius: 0 8px 8px 0;
          padding: 12px 18px;
          margin: 16px 0;
          white-space: pre-wrap;
          word-break: break-word;
        }
      `}</style>
    </div>
  );
}
